/*
  Iter122k125 · Credius · Sezione "Recuperi" nella scheda pratica.
  Attiva solo se `pratica.settore === "Recupero crediti"`.
  Consente ad admin/avvocato affidatario/co-affidatario di:
    - Registrare posizioni debitorie (una per ciascuna controparte insoluta)
    - Registrare i pagamenti (parziali/saldo) → auto-transizione a "Chiusa Saldata" (verde)
    - Creare/aggiornare piano di rientro (elenco ratei) auto-generato o manuale
    - Chiudere forzatamente (irrecuperabile) o passare a giudiziale
  Il CLIENTE della pratica vede tutto ma in sola lettura (nessun bottone editing).
*/
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Handshake, PlusCircle, Loader2, Euro, Trash2, Pencil, CalendarClock, ArrowUpRight,
  XCircle, RotateCcw, Info, CheckCircle2, PhoneCall,
} from "lucide-react";

const fmtEUR = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

const STATO_STYLE = {
  "Aperta": "bg-red-100 text-red-800 border-red-300",
  "In piano di rientro": "bg-amber-100 text-amber-800 border-amber-300",
  "Chiusa Saldata": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Chiusa Irrecuperabile": "bg-slate-200 text-slate-700 border-slate-300",
  "Passata a giudiziale": "bg-indigo-100 text-indigo-800 border-indigo-300",
};

const CADENZE = [
  { v: "settimanale", label: "Settimanale" },
  { v: "quindicinale", label: "Quindicinale" },
  { v: "mensile", label: "Mensile" },
  { v: "bimestrale", label: "Bimestrale" },
  { v: "trimestrale", label: "Trimestrale" },
  { v: "semestrale", label: "Semestrale" },
  { v: "annuale", label: "Annuale" },
];

export function CrediusRecuperi({ pratica }) {
  const pid = pratica?.id;
  const controparti = pratica?.controparti || [];
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCrea, setShowCrea] = useState(false);
  const [showPag, setShowPag] = useState(null);   // pos_id (creazione)
  const [editPag, setEditPag] = useState(null);   // { posid, pag } — Iter122k135 · modifica pagamento
  const [editPos, setEditPos] = useState(null);   // pos — Iter122k136 · modifica posizione
  const [showPiano, setShowPiano] = useState(null); // pos_id
  const [showChiudi, setShowChiudi] = useState(null); // pos_id

  const load = () => api.get(`/credius/${pid}/recuperi`).then((r) => setData(r.data)).catch(() => setData({ posizioni: [], riepilogo: {}, can_edit: false }));
  useEffect(() => { if (pid) load(); }, [pid]); // eslint-disable-line

  const canEdit = data?.can_edit;

  if (!data) return (
    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-red-800" /></div>
  );

  const r = data.riepilogo || {};
  return (
    <div className="space-y-4" data-testid="credius-recuperi">
      {/* Riepilogo */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <StatCard label="Posizioni totali" value={r.totale_posizioni || 0} isCount />
        <StatCard label="Aperte" value={r.aperte || 0} color="red" isCount />
        <StatCard label="Saldate" value={r.saldate || 0} color="emerald" isCount />
        <StatCard label="Debito totale" value={r.totale_debito || 0} color="indigo" />
        <StatCard label="Tot. recuperato" value={r.totale_incassato || 0} color="emerald" testid="recuperi-tot-recuperato" />
        <StatCard label="Residuo" value={r.residuo_totale || 0} color="amber" />
      </div>

      {r.tutte_chiuse && canEdit && (
        <div data-testid="recuperi-banner-tutte-chiuse"
          className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <b className="text-emerald-800">Tutte le posizioni sono chiuse.</b>{" "}
            <span className="text-slate-700">Puoi valutare di chiudere l'intera pratica di affido dalla sezione "Gestione pratica".</span>
          </div>
        </div>
      )}

      {/* Header + azione */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-slate-600 inline-flex items-center gap-1">
          <Info className="w-4 h-4" />
          Per ogni <b>controparte insolvente</b>, registra il debito, i pagamenti e l'eventuale piano di rientro.
        </div>
        {canEdit && (
          <Button size="sm" data-testid="recuperi-nuova-btn"
            onClick={() => setShowCrea(true)}
            className="bg-red-700 hover:bg-red-800">
            <PlusCircle className="w-4 h-4 mr-1" /> Nuova posizione
          </Button>
        )}
      </div>

      {/* Elenco posizioni */}
      {(data.posizioni || []).length === 0 ? (
        <div data-testid="recuperi-empty" className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-slate-500">
          <Handshake className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessuna posizione di recupero registrata.
        </div>
      ) : (
        <div className="space-y-3">
          {data.posizioni.map((pos) => (
            <PosizioneCard key={pos.id} pos={pos} canEdit={canEdit} busy={busy} setBusy={setBusy}
              onReload={load} pid={pid}
              onPag={() => setShowPag(pos.id)}
              onEditPag={(pag) => setEditPag({ posid: pos.id, pag })}
              onEditPos={() => setEditPos(pos)}
              onPiano={() => setShowPiano(pos.id)}
              onChiudi={() => setShowChiudi(pos.id)} />
          ))}
        </div>
      )}

      {showCrea && (
        <CreaPosizioneDialog pid={pid} controparti={controparti}
          onClose={() => setShowCrea(false)} onCreated={() => { setShowCrea(false); load(); }} />
      )}
      {showPag && (
        <PagamentoDialog pid={pid} posid={showPag}
          onClose={() => setShowPag(null)} onDone={() => { setShowPag(null); load(); }} />
      )}
      {editPag && (
        <PagamentoDialog pid={pid} posid={editPag.posid} pag={editPag.pag}
          onClose={() => setEditPag(null)} onDone={() => { setEditPag(null); load(); }} />
      )}
      {editPos && (
        <ModificaPosizioneDialog pid={pid} pos={editPos}
          onClose={() => setEditPos(null)} onDone={() => { setEditPos(null); load(); }} />
      )}
      {showPiano && (
        <PianoDialog pid={pid} pos={data.posizioni.find((p) => p.id === showPiano)}
          onClose={() => setShowPiano(null)} onDone={() => { setShowPiano(null); load(); }} />
      )}
      {showChiudi && (
        <ChiudiForzataDialog pid={pid} posid={showChiudi}
          onClose={() => setShowChiudi(null)} onDone={() => { setShowChiudi(null); load(); }} />
      )}
    </div>
  );
}


function PosizioneCard({ pos, canEdit, busy, setBusy, onReload, pid, onPag, onEditPag, onEditPos, onPiano, onChiudi }) {
  const trasformaGiud = async () => {
    const note = window.prompt("Note (opzionali) per la trasformazione a giudiziale:", "");
    if (note === null) return;
    setBusy(true);
    try {
      await api.post(`/credius/${pid}/recuperi/${pos.id}/trasforma-giudiziale`, { note });
      toast.success("Posizione passata a giudiziale");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  const riapri = async () => {
    if (!window.confirm("Riaprire la posizione? Sarà nuovamente possibile registrare pagamenti.")) return;
    setBusy(true);
    try {
      await api.post(`/credius/${pid}/recuperi/${pos.id}/riapri`);
      toast.success("Posizione riaperta"); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  const elimina = async () => {
    if (!window.confirm("Eliminare definitivamente questa posizione?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/recuperi/${pos.id}`);
      toast.success("Posizione eliminata"); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  // Iter123k226 · eliminazione dedicata del piano di rientro (richiesta utente).
  const eliminaPiano = async () => {
    if (!window.confirm("Eliminare il piano di rientro? La posizione resterà aperta " +
                          "e potrai crearne uno nuovo in qualsiasi momento.")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/recuperi/${pos.id}/piano-rientro`);
      toast.success("Piano di rientro eliminato");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione piano fallita");
    } finally { setBusy(false); }
  };

  const rimuoviPag = async (pagid) => {
    if (!window.confirm("Rimuovere questo pagamento?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/recuperi/${pos.id}/pagamenti/${pagid}`);
      toast.success("Pagamento rimosso"); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };

  // Iter123k168 · Home Collection · solleciti telefonici post-raccomandata
  const oggi = new Date().toISOString().slice(0, 10);
  const [hcOpen, setHcOpen] = useState(false);
  const [hcData, setHcData] = useState(oggi);
  const [hcEsito, setHcEsito] = useState("");
  const [hcEditId, setHcEditId] = useState(null);
  const salvaHc = async () => {
    if (!hcData) { toast.error("Inserisci la data del contatto"); return; }
    setBusy(true);
    try {
      if (hcEditId) {
        await api.patch(`/credius/${pid}/recuperi/${pos.id}/home-collection/${hcEditId}`,
          { data: hcData, esito: hcEsito });
        toast.success("Sollecito aggiornato");
      } else {
        await api.post(`/credius/${pid}/recuperi/${pos.id}/home-collection`,
          { data: hcData, esito: hcEsito });
        toast.success("Sollecito registrato");
      }
      setHcOpen(false); setHcEditId(null); setHcData(oggi); setHcEsito("");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  const modificaHc = (h) => {
    setHcEditId(h.id); setHcData(h.data); setHcEsito(h.esito || ""); setHcOpen(true);
  };
  const rimuoviHc = async (hcid) => {
    if (!window.confirm("Rimuovere questo sollecito?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/${pid}/recuperi/${pos.id}/home-collection/${hcid}`);
      toast.success("Sollecito rimosso"); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };

  const isChiusa = pos.stato === "Chiusa Irrecuperabile" || pos.stato === "Passata a giudiziale";
  const isSaldata = pos.stato === "Chiusa Saldata";

  return (
    <div data-testid={`recupero-pos-${pos.id}`}
      className={`rounded-xl border-2 bg-white p-3 space-y-2 ${isSaldata ? "border-emerald-300" : isChiusa ? "border-slate-300" : pos.stato === "In piano di rientro" ? "border-amber-300" : "border-red-300"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[pos.stato] || "bg-slate-100 border-slate-300"}`}>
          {pos.stato}
        </span>
        <span className="font-bold text-slate-900">{pos.soggetto_nome}</span>
        {pos.descrizione && <span className="text-xs text-slate-500 italic">· {pos.descrizione}</span>}
        {pos.data_scadenza && <span className="text-xs text-slate-500">· scad. {fmtD(pos.data_scadenza)}</span>}
        <span className="ml-auto text-sm">
          <span className="text-slate-500">Debito:</span> <b>{fmtEUR(pos.importo_debito)}</b>
        </span>
      </div>

      {/* Progresso */}
      <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
        <span>Incassato: <b className="text-emerald-700">{fmtEUR(pos.totale_pagato)}</b></span>
        <span>Residuo: <b className={pos.residuo > 0 ? "text-red-700" : "text-emerald-700"}>{fmtEUR(pos.residuo)}</b></span>
        {pos.chiusura_forzata_motivo && (
          <span className="text-slate-500 italic">Motivo chiusura: {pos.chiusura_forzata_motivo}</span>
        )}
        {pos.passata_giudiziale_note && (
          <span className="text-slate-500 italic">Nota giudiziale: {pos.passata_giudiziale_note}</span>
        )}
      </div>

      {/* Pagamenti */}
      {(pos.pagamenti || []).length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
          <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Pagamenti</div>
          <ul className="text-xs space-y-0.5">
            {pos.pagamenti.map((pg) => (
              <li key={pg.id} className="flex items-center gap-2" data-testid={`pag-${pg.id}`}>
                <Euro className="w-3 h-3 text-emerald-700" />
                <span>{fmtD(pg.data)}</span>
                <b>{fmtEUR(pg.importo)}</b>
                {pg.metodo && <span className="text-slate-500">({pg.metodo})</span>}
                {pg.nota && <span className="text-slate-500 italic">— {pg.nota}</span>}
                {canEdit && (
                  <span className="ml-auto flex items-center gap-1.5">
                    <button type="button" onClick={() => onEditPag(pg)}
                      data-testid={`pag-edit-${pg.id}`}
                      title="Modifica pagamento"
                      className="text-indigo-600 hover:text-indigo-800">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => rimuoviPag(pg.id)}
                      data-testid={`pag-del-${pg.id}`}
                      title="Rimuovi pagamento"
                      className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Iter123k168 · Home Collection · solleciti telefonici post-raccomandata */}
      <div className="rounded-lg bg-sky-50 border border-sky-200 p-2" data-testid={`home-collection-${pos.id}`}>
        <div className="flex items-center gap-2 mb-1">
          <PhoneCall className="w-3.5 h-3.5 text-sky-800" />
          <span className="text-[10px] font-bold uppercase text-sky-800">
            Phone Collection {(pos.home_collection || []).length > 0 && `· ${pos.home_collection.length} ${pos.home_collection.length === 1 ? "sollecito" : "solleciti"}`}
          </span>
          {canEdit && (
            <button type="button"
              data-testid={`hc-add-btn-${pos.id}`}
              onClick={() => { setHcEditId(null); setHcData(oggi); setHcEsito(""); setHcOpen(!hcOpen); }}
              className="ml-auto text-[11px] font-bold text-sky-800 hover:text-sky-900 inline-flex items-center gap-1">
              <PlusCircle className="w-3.5 h-3.5" /> {hcOpen ? "Chiudi" : "Aggiungi sollecito"}
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-500 italic mb-2">
          Registro dei solleciti telefonici eseguiti dopo l'invio della raccomandata.
        </p>
        {hcOpen && canEdit && (
          <div className="rounded-md bg-white border border-sky-200 p-2 mb-2 space-y-2" data-testid={`hc-form-${pos.id}`}>
            <div className="grid sm:grid-cols-[auto,1fr] gap-2 items-start">
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Data contatto</Label>
                <Input type="date" value={hcData} onChange={(e) => setHcData(e.target.value)}
                  data-testid={`hc-data-${pos.id}`} className="h-9 text-sm mt-1" />
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-slate-600">Nota esito contatto</Label>
                <Textarea value={hcEsito} onChange={(e) => setHcEsito(e.target.value)}
                  data-testid={`hc-esito-${pos.id}`}
                  placeholder="Es. Contattato debitore, promesso pagamento entro 15gg..."
                  className="min-h-[60px] text-sm mt-1" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => { setHcOpen(false); setHcEditId(null); }} disabled={busy}>
                Annulla
              </Button>
              <Button size="sm" onClick={salvaHc} disabled={busy || !hcData}
                data-testid={`hc-save-btn-${pos.id}`}
                className="bg-sky-700 hover:bg-sky-800">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                {hcEditId ? "Aggiorna" : "Salva sollecito"}
              </Button>
            </div>
          </div>
        )}
        {(pos.home_collection || []).length === 0 ? (
          !hcOpen && <div className="text-[11px] text-slate-500 italic">Nessun sollecito registrato.</div>
        ) : (
          <ul className="text-xs space-y-1">
            {(pos.home_collection || []).map((h) => (
              <li key={h.id} className="flex items-start gap-2 bg-white rounded-md border border-sky-100 px-2 py-1.5"
                  data-testid={`hc-item-${h.id}`}>
                <PhoneCall className="w-3 h-3 text-sky-700 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <b className="text-slate-800">{fmtD(h.data)}</b>
                    {h.created_by_nome && (
                      <span className="text-[10px] text-slate-400">— {h.created_by_nome}</span>
                    )}
                  </div>
                  {h.esito && (
                    <div className="text-slate-600 whitespace-pre-wrap mt-0.5">{h.esito}</div>
                  )}
                </div>
                {canEdit && (
                  <span className="flex items-center gap-1.5 shrink-0">
                    <button type="button" onClick={() => modificaHc(h)}
                      data-testid={`hc-edit-${h.id}`}
                      title="Modifica sollecito"
                      className="text-indigo-600 hover:text-indigo-800">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => rimuoviHc(h.id)}
                      data-testid={`hc-del-${h.id}`}
                      title="Rimuovi sollecito"
                      className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>


      {/* Piano di rientro */}
      {pos.piano_rientro && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2">
          <div className="text-[10px] font-bold uppercase text-amber-800 mb-1 flex items-center justify-between gap-1">
            <span className="flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" />
              Piano di rientro {pos.piano_rientro.generato_auto ? "(auto)" : "(manuale)"} · {pos.piano_rientro.ratei?.length} ratei
            </span>
            {canEdit && (
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`piano-edit-btn-${pos.id}`}
                  onClick={onPiano} disabled={busy}
                  title="Modifica il piano"
                  className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 p-0.5 rounded">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" data-testid={`piano-del-btn-${pos.id}`}
                  onClick={eliminaPiano} disabled={busy}
                  title="Elimina il piano"
                  className="text-red-700 hover:text-red-900 hover:bg-red-100 p-0.5 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {pos.piano_rientro.warning_sbilanciato && (
            <div data-testid={`piano-warning-sbilanciato-${pos.id}`}
              className="mt-1 mb-2 rounded-md border-2 border-orange-400 bg-orange-50 text-orange-900 text-[11px] px-2 py-1 flex items-start gap-1.5">
              <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-600" />
              <span>
                <b>Piano sbilanciato:</b> la somma dei ratei ({fmtEUR(pos.piano_rientro.somma_ratei || 0)})
                differisce dal residuo ({fmtEUR(pos.residuo)}) di{" "}
                <b>{fmtEUR(Math.abs(pos.piano_rientro.delta_residuo || 0))}</b>.
                {pos.piano_rientro.delta_residuo > 0 ? " Ratei in eccesso." : " Ratei insufficienti a coprire il debito."}
              </span>
            </div>
          )}
          <ul className="text-xs grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {pos.piano_rientro.ratei.map((rt) => (
              <li key={rt.id || rt.numero} className="flex items-center gap-2" data-testid={`rateo-${pos.id}-${rt.numero}`}>
                <span className="font-mono text-[10px] text-slate-500">#{rt.numero}</span>
                <span>{fmtD(rt.data_scadenza)}</span>
                <b>{fmtEUR(rt.importo)}</b>
                <span className={`text-[10px] font-semibold ${rt.stato === "Incassato" ? "text-emerald-700" : rt.stato === "Scaduto" ? "text-red-700" : "text-slate-500"}`}>
                  {rt.stato}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Azioni */}
      {canEdit && (
        <div className="flex flex-wrap gap-2 pt-1">
          {!isChiusa && !isSaldata && (
            <>
              <Button size="sm" data-testid={`pos-pag-btn-${pos.id}`} onClick={onPag}
                disabled={busy} className="bg-emerald-700 hover:bg-emerald-800">
                <Euro className="w-4 h-4 mr-1" /> Registra pagamento
              </Button>
              <Button size="sm" variant="outline" data-testid={`pos-piano-btn-${pos.id}`} onClick={onPiano}
                disabled={busy} className="border-amber-300 text-amber-800 hover:bg-amber-50">
                <CalendarClock className="w-4 h-4 mr-1" /> Piano di rientro
              </Button>
              <Button size="sm" variant="outline" data-testid={`pos-chiudi-btn-${pos.id}`} onClick={onChiudi}
                disabled={busy} className="border-slate-400 text-slate-700 hover:bg-slate-100">
                <XCircle className="w-4 h-4 mr-1" /> Chiudi (irrecuperabile)
              </Button>
              <Button size="sm" variant="outline" data-testid={`pos-giud-btn-${pos.id}`} onClick={trasformaGiud}
                disabled={busy} className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                <ArrowUpRight className="w-4 h-4 mr-1" /> Trasforma in giudiziale
              </Button>
            </>
          )}
          {isChiusa && (
            <Button size="sm" variant="outline" data-testid={`pos-riapri-btn-${pos.id}`} onClick={riapri}
              disabled={busy} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <RotateCcw className="w-4 h-4 mr-1" /> Riapri posizione
            </Button>
          )}
          <Button size="sm" variant="outline" data-testid={`pos-edit-btn-${pos.id}`} onClick={onEditPos}
            disabled={busy} className="border-indigo-300 text-indigo-800 hover:bg-indigo-50 ml-auto">
            <Pencil className="w-4 h-4 mr-1" /> Modifica
          </Button>
          <Button size="sm" variant="outline" data-testid={`pos-del-btn-${pos.id}`} onClick={elimina}
            disabled={busy} className="border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1" /> Elimina
          </Button>
        </div>
      )}
    </div>
  );
}


function CreaPosizioneDialog({ pid, controparti, onClose, onCreated }) {
  const [ref, setRef] = useState("");
  const [imp, setImp] = useState("");
  const [desc, setDesc] = useState("");
  const [scad, setScad] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/credius/${pid}/recuperi`, {
        soggetto_ref_id: ref, importo_debito: Number(imp), descrizione: desc, data_scadenza: scad,
      });
      toast.success("Posizione creata"); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione fallita"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Nuova posizione di recupero">
      <div className="space-y-3">
        <div>
          <Label className="text-xs uppercase">Controparte insolvente *</Label>
          <select value={ref} onChange={(e) => setRef(e.target.value)}
            data-testid="crea-pos-ref" className="w-full h-10 mt-1 border border-slate-300 rounded-md px-2">
            <option value="">— Seleziona —</option>
            {controparti.length === 0 && <option disabled>Aggiungi prima almeno una controparte alla pratica</option>}
            {controparti.map((c, i) => (
              <option key={c.ref_id || c.id || i} value={c.ref_id || c.id}>{c.nome || c.ragione_sociale}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase">Importo debito (€) *</Label>
            <Input data-testid="crea-pos-importo" type="number" step="0.01" value={imp}
              onChange={(e) => setImp(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase">Data scadenza (opz.)</Label>
            <Input type="date" value={scad} onChange={(e) => setScad(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase">Descrizione insoluto</Label>
          <Input data-testid="crea-pos-desc" placeholder="Es. Fattura n. 34/2025 · Quote condominiali 2024/2025"
            value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="crea-pos-submit" onClick={submit}
            disabled={busy || !ref || !Number(imp)} className="bg-red-700 hover:bg-red-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-1" />}
            Crea posizione
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function ModificaPosizioneDialog({ pid, pos, onClose, onDone }) {
  // Iter122k136 · modifica posizione (importo/descrizione/scadenza)
  const [imp, setImp] = useState(pos?.importo_debito != null ? String(pos.importo_debito) : "");
  const [desc, setDesc] = useState(pos?.descrizione || "");
  const [scad, setScad] = useState(pos?.data_scadenza || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/credius/${pid}/recuperi/${pos.id}`, {
        importo_debito: Number(imp),
        descrizione: desc,
        data_scadenza: scad || "",
      });
      toast.success("Posizione aggiornata"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Modifica fallita"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Modifica posizione · ${pos?.soggetto_nome || ""}`}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase">Importo debito (€) *</Label>
            <Input data-testid="edit-pos-importo" type="number" step="0.01" value={imp}
              onChange={(e) => setImp(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase">Data scadenza (opz.)</Label>
            <Input type="date" data-testid="edit-pos-scad" value={scad}
              onChange={(e) => setScad(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase">Descrizione insoluto</Label>
          <Input data-testid="edit-pos-desc" placeholder="Es. Fattura n. 34/2025 · Quote condominiali 2024/2025"
            value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1" />
        </div>
        {Number(pos?.totale_pagato) > 0 && (
          <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
            <span>Su questa posizione sono stati registrati <b>{fmtEUR(pos.totale_pagato)}</b> di pagamenti. Modificando l'importo, residuo e stato verranno ricalcolati automaticamente.</span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="edit-pos-submit" onClick={submit}
            disabled={busy || !Number(imp)} className="bg-indigo-700 hover:bg-indigo-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}
            Salva modifiche
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function PagamentoDialog({ pid, posid, pag, onClose, onDone }) {
  // Iter122k135 · se `pag` è passato, siamo in modalità modifica
  const isEdit = !!pag;
  const [d, setD] = useState(pag?.data || new Date().toISOString().slice(0, 10));
  const [imp, setImp] = useState(pag?.importo != null ? String(pag.importo) : "");
  const [met, setMet] = useState(pag?.metodo || "Bonifico");
  const [nota, setNota] = useState(pag?.nota || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { data: d, importo: Number(imp), metodo: met, nota };
      if (isEdit) {
        await api.patch(`/credius/${pid}/recuperi/${posid}/pagamenti/${pag.id}`, payload);
        toast.success("Pagamento aggiornato");
      } else {
        await api.post(`/credius/${pid}/recuperi/${posid}/pagamenti`, payload);
        toast.success("Pagamento registrato");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || (isEdit ? "Modifica fallita" : "Registrazione fallita")); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={isEdit ? "Modifica pagamento debitore" : "Registra pagamento debitore"}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase">Data pagamento *</Label>
            <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="mt-1" data-testid="pag-data" />
          </div>
          <div>
            <Label className="text-xs uppercase">Importo (€) *</Label>
            <Input type="number" step="0.01" data-testid="pag-importo"
              value={imp} onChange={(e) => setImp(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase">Metodo</Label>
          <select value={met} onChange={(e) => setMet(e.target.value)}
            className="w-full h-10 mt-1 border border-slate-300 rounded-md px-2">
            <option>Bonifico</option><option>Contanti</option><option>Assegno</option>
            <option>Carta</option><option>Altro</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase">Nota (opz.)</Label>
          <Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Es. Acconto quote 2024" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="pag-submit" onClick={submit} disabled={busy || !Number(imp)}
            className="bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Euro className="w-4 h-4 mr-1" />}
            {isEdit ? "Salva modifiche" : "Registra"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// Iter122k132 · Genera ratei lato client (stessa logica del backend, arrotondamento sull'ultimo)
function generateRateiLocalmente(importo, num, dataInizio, cadenza) {
  const d0 = new Date(dataInizio + "T00:00:00");
  const quota = Math.round((importo / num) * 100) / 100;
  const monthsMap = { mensile: 1, bimestrale: 2, trimestrale: 3, semestrale: 6, annuale: 12 };
  const out = [];
  for (let i = 0; i < num; i++) {
    const d = new Date(d0);
    if (cadenza === "settimanale") d.setDate(d0.getDate() + 7 * i);
    else if (cadenza === "quindicinale") d.setDate(d0.getDate() + 14 * i);
    else d.setMonth(d0.getMonth() + (monthsMap[cadenza] || 1) * i);
    out.push({
      numero: i + 1,
      data_scadenza: d.toISOString().slice(0, 10),
      importo: quota, stato: "Da incassare",
    });
  }
  const somma = out.reduce((s, r) => s + r.importo, 0);
  const diff = Math.round((importo - somma) * 100) / 100;
  if (diff && out.length) out[out.length - 1].importo = Math.round((out[out.length - 1].importo + diff) * 100) / 100;
  return out;
}


function PianoDialog({ pid, pos, onClose, onDone }) {
  // Iter122k132 · Se la posizione ha già un piano, si parte in edit dei ratei esistenti.
  const hasPiano = !!(pos?.piano_rientro?.ratei || []).length;
  const [mode, setMode] = useState(hasPiano ? "manuale" : "auto"); // auto (setup) | manuale (edit ratei)
  const [imp, setImp] = useState(pos?.residuo || pos?.importo_debito || "");
  const [n, setN] = useState(6);
  const [inizio, setInizio] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [cad, setCad] = useState("mensile");
  const [nota, setNota] = useState(pos?.piano_rientro?.nota || "");
  // Iter123k226 · normalizza stati rateo legacy (es. "Pagato") sugli stati accettati
  // dal backend ("Da incassare" / "Incassato" / "Scaduto"), altrimenti il PUT fallisce 400.
  const _normStato = (s) => {
    if (!s) return "Da incassare";
    const low = String(s).trim().toLowerCase();
    if (["pagato", "pagata", "incassato", "incassata", "saldato", "saldata"].includes(low)) return "Incassato";
    if (["scaduto", "scaduta", "insoluto", "insoluta"].includes(low)) return "Scaduto";
    if (["da incassare", "aperto", "aperta", "da pagare"].includes(low)) return "Da incassare";
    return "Da incassare";
  };
  const [manuali, setManuali] = useState((pos?.piano_rientro?.ratei || []).map((r) => ({
    numero: r.numero, data_scadenza: r.data_scadenza,
    importo: r.importo, stato: _normStato(r.stato),
  })));
  const [busy, setBusy] = useState(false);

  const totaleRatei = manuali.reduce((s, r) => s + (Number(r.importo) || 0), 0);

  const addRateo = () => setManuali((rs) => [...rs, {
    numero: rs.length + 1,
    data_scadenza: new Date(Date.now() + (30 * (rs.length + 1)) * 86400000).toISOString().slice(0, 10),
    importo: 0, stato: "Da incassare",
  }]);
  const rmRateo = (i) => setManuali((rs) => rs.filter((_, k) => k !== i).map((r, k) => ({ ...r, numero: k + 1 })));

  // Iter122k132 · "Genera anteprima" calcola i ratei localmente e passa in modalità editing
  const generaAnteprima = () => {
    const imp_ = Number(imp), n_ = Number(n);
    if (!(imp_ > 0) || !(n_ >= 1 && n_ <= 240) || !inizio) {
      toast.error("Compila importo, numero ratei (1-240) e data inizio");
      return;
    }
    setManuali(generateRateiLocalmente(imp_, n_, inizio, cad));
    setMode("manuale");
  };

  const submit = async () => {
    if (!manuali.length) {
      toast.error("Il piano non contiene alcun rateo");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/credius/${pid}/recuperi/${pos.id}/piano-rientro`, {
        ratei: manuali.map((r) => ({
          numero: Number(r.numero), data_scadenza: r.data_scadenza,
          importo: Number(r.importo), stato: r.stato,
        })), nota,
      });
      toast.success("Piano di rientro salvato"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio fallito"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={`Piano di rientro · ${pos?.soggetto_nome || ""}`}>
      <div className="space-y-3">
        <div className="flex gap-2">
          <button type="button" data-testid="piano-tab-auto"
            onClick={() => setMode("auto")}
            className={`px-3 py-1.5 rounded-md text-sm font-bold border ${mode === "auto" ? "bg-amber-100 text-amber-800 border-amber-300" : "border-slate-300 text-slate-600"}`}>
            Auto-genera
          </button>
          <button type="button" data-testid="piano-tab-manuale"
            onClick={() => setMode("manuale")}
            className={`px-3 py-1.5 rounded-md text-sm font-bold border ${mode === "manuale" ? "bg-amber-100 text-amber-800 border-amber-300" : "border-slate-300 text-slate-600"}`}>
            Elenco rate {manuali.length > 0 ? `(${manuali.length})` : ""}
          </button>
        </div>

        {mode === "auto" ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Importo totale (€) *</Label>
                <Input type="number" step="0.01" data-testid="piano-auto-importo"
                  value={imp} onChange={(e) => setImp(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase">Numero ratei *</Label>
                <Input type="number" min={1} max={240} data-testid="piano-auto-num"
                  value={n} onChange={(e) => setN(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase">Data inizio *</Label>
                <Input type="date" data-testid="piano-auto-inizio"
                  value={inizio} onChange={(e) => setInizio(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase">Cadenza *</Label>
                <select data-testid="piano-auto-cad" value={cad}
                  onChange={(e) => setCad(e.target.value)}
                  className="w-full h-10 mt-1 border border-slate-300 rounded-md px-2">
                  {CADENZE.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <Button size="sm" data-testid="piano-genera-anteprima" onClick={generaAnteprima}
              className="bg-amber-600 hover:bg-amber-700">
              <CalendarClock className="w-4 h-4 mr-1" /> Genera anteprima ratei
            </Button>
            <div className="text-xs text-slate-500">
              Dopo la generazione potrai modificare manualmente gli importi delle singole rate.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="piano-ratei-editor">
            <div className="text-xs text-slate-500">
              Modifica gli importi delle singole rate. Il totale è calcolato automaticamente.
            </div>
            {manuali.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`piano-rateo-row-${i}`}>
                <span className="col-span-1 font-mono text-xs text-slate-500">#{r.numero}</span>
                <Input type="date" className="col-span-4"
                  value={r.data_scadenza}
                  onChange={(e) => setManuali((rs) => rs.map((x, k) => k === i ? { ...x, data_scadenza: e.target.value } : x))} />
                <Input type="number" step="0.01" className="col-span-3"
                  data-testid={`piano-rateo-importo-${i}`}
                  placeholder="importo"
                  value={r.importo}
                  onChange={(e) => setManuali((rs) => rs.map((x, k) => k === i ? { ...x, importo: e.target.value } : x))} />
                <select className="col-span-3 h-10 border border-slate-300 rounded-md px-2"
                  value={r.stato}
                  onChange={(e) => setManuali((rs) => rs.map((x, k) => k === i ? { ...x, stato: e.target.value } : x))}>
                  <option>Da incassare</option><option>Incassato</option><option>Scaduto</option>
                </select>
                <button type="button" onClick={() => rmRateo(i)} className="col-span-1 text-red-600" title="Rimuovi rateo"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addRateo}
              data-testid="piano-man-add" className="border-amber-300 text-amber-800">
              <PlusCircle className="w-4 h-4 mr-1" /> Aggiungi rateo
            </Button>
            {/* Iter122k132 · Totale sommatoria ratei live */}
            <div data-testid="piano-ratei-totale"
              className="mt-2 flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <span className="text-xs uppercase font-bold text-emerald-800">Totale ratei</span>
              <span className="text-lg font-bold text-emerald-900">
                {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(totaleRatei)}
              </span>
              {Number(imp) > 0 && Math.abs(Number(imp) - totaleRatei) > 0.01 && (
                <span className="text-[10px] text-amber-700 italic ml-2">
                  Δ vs importo target ({new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(imp))}):{" "}
                  {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", signDisplay: "always" }).format(totaleRatei - Number(imp))}
                </span>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase">Nota (opz.)</Label>
              <Input value={nota} onChange={(e) => setNota(e.target.value)} className="mt-1" />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="piano-submit" onClick={submit}
            disabled={busy || mode === "auto" || !manuali.length}
            className="bg-amber-600 hover:bg-amber-700">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1" />}
            Salva piano
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function ChiudiForzataDialog({ pid, posid, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/credius/${pid}/recuperi/${posid}/chiudi-forzata`, { motivo });
      toast.success("Posizione chiusa"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione fallita"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Chiudi posizione (irrecuperabile)">
      <div className="space-y-3">
        <div className="text-sm text-slate-600">
          Indica il motivo per cui il credito è considerato definitivamente <b>irrecuperabile</b>
          (nullatenente accertato, prescrizione, insolvibilità, ecc.).
        </div>
        <Textarea rows={3} value={motivo} data-testid="chiudi-motivo"
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo obbligatorio…" />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button data-testid="chiudi-submit" onClick={submit}
            disabled={busy || !motivo.trim()} className="bg-slate-700 hover:bg-slate-800">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
            Conferma chiusura
          </Button>
        </div>
      </div>
    </Modal>
  );
}


function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-indigo-900">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}


function StatCard({ label, value, color = "indigo", isCount = false, testid }) {
  const bg = {
    red: "bg-red-50 border-red-300", amber: "bg-amber-50 border-amber-300",
    emerald: "bg-emerald-50 border-emerald-300", indigo: "bg-indigo-50 border-indigo-300",
  }[color] || "bg-white";
  const text = {
    red: "text-red-800", amber: "text-amber-800",
    emerald: "text-emerald-800", indigo: "text-indigo-900",
  }[color] || "text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${text}`}>{isCount ? value : fmtEUR(value)}</div>
    </div>
  );
}
